/* Where the sign-in code went (Oct 2026, decision D6, from the signed-off mock WaOtp_1):
   ① "We sent a 6-digit code to your WhatsApp", ② after 20 seconds "Didn't get it? Send by
   SMS", ③ "We sent a 6-digit code by SMS". A code WhatsApp cannot deliver is sent by SMS
   by the server on its own; the link is for everyone else who is still waiting. */
import { useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";

export const SMS_AFTER_SECONDS = 20;

const TEXT = {
  en: { wa: "We sent a 6-digit code to your WhatsApp: {phone}", sms: "We sent a 6-digit code by SMS to {phone}",
    wait: "Didn't get it? You can ask for SMS in {n} seconds", ask: "Didn't get it? Send by SMS" },
  te: { wa: "మీ వాట్సాప్‌కి 6 అంకెల కోడ్ పంపాం: {phone}", sms: "ఎస్ఎంఎస్‌లో 6 అంకెల కోడ్ పంపాం: {phone}",
    wait: "కోడ్ రాలేదా? {n} సెకన్లలో ఎస్ఎంఎస్‌లో పంపే ఎంపిక వస్తుంది", ask: "కోడ్ రాలేదా? ఎస్ఎంఎస్‌లో పంపండి" },
  hi: { wa: "आपके व्हाट्सऐप पर 6 अंकों का कोड भेजा है: {phone}", sms: "एसएमएस से 6 अंकों का कोड भेजा है: {phone}",
    wait: "कोड नहीं मिला? {n} सेकंड में एसएमएस से मँगाने का विकल्प आएगा", ask: "कोड नहीं मिला? एसएमएस से भेजें" },
  ta: { wa: "உங்கள் வாட்ஸ்அப்புக்கு 6 இலக்க குறியீடு அனுப்பினோம்: {phone}", sms: "எஸ்எம்எஸ் மூலம் 6 இலக்க குறியீடு அனுப்பினோம்: {phone}",
    wait: "குறியீடு வரவில்லையா? {n} விநாடிகளில் எஸ்எம்எஸ் மூலம் பெறும் வசதி வரும்", ask: "குறியீடு வரவில்லையா? எஸ்எம்எஸ் மூலம் அனுப்பவும்" },
  kn: { wa: "ನಿಮ್ಮ ವಾಟ್ಸಾಪ್‌ಗೆ 6 ಅಂಕಿಯ ಕೋಡ್ ಕಳುಹಿಸಿದ್ದೇವೆ: {phone}", sms: "ಎಸ್‌ಎಂಎಸ್ ಮೂಲಕ 6 ಅಂಕಿಯ ಕೋಡ್ ಕಳುಹಿಸಿದ್ದೇವೆ: {phone}",
    wait: "ಕೋಡ್ ಬರಲಿಲ್ಲವೇ? {n} ಸೆಕೆಂಡುಗಳಲ್ಲಿ ಎಸ್‌ಎಂಎಸ್ ಮೂಲಕ ಪಡೆಯುವ ಆಯ್ಕೆ ಬರುತ್ತದೆ", ask: "ಕೋಡ್ ಬರಲಿಲ್ಲವೇ? ಎಸ್‌ಎಂಎಸ್ ಮೂಲಕ ಕಳುಹಿಸಿ" },
};
export const OTP_CHANNEL_TEXT = TEXT;

/* "Send code via WhatsApp | Text Message" before the code is asked for (31 Oct 2026): the
   person picks; the choice is remembered on the phone and every code request sends it
   (services/authService, hooks/useOtpVerification). Text Message (our SRMIKA header) by
   default since 4 Nov 2026 (choice "B"); the server falls back to WhatsApp. */
const PICK_TEXT = {
  en: { via: "Send code via", wa: "WhatsApp", sms: "Text Message" },
  te: { via: "కోడ్ ఎలా పంపాలి", wa: "వాట్సాప్", sms: "ఎస్ఎంఎస్" },
  hi: { via: "कोड कैसे भेजें", wa: "व्हाट्सऐप", sms: "एसएमएस" },
  ta: { via: "குறியீட்டை எப்படி அனுப்புவது", wa: "வாட்ஸ்அப்", sms: "எஸ்எம்எஸ்" },
  kn: { via: "ಕೋಡ್ ಹೇಗೆ ಕಳುಹಿಸಬೇಕು", wa: "ವಾಟ್ಸಾಪ್", sms: "ಎಸ್‌ಎಂಎಸ್" },
};
const PICK_KEY = "otp_channel";
/** The channel picked on this phone: "SMS" (the default) or "WHATSAPP". */
export const pickedOtpChannel = () => { try { return localStorage.getItem(PICK_KEY) === "WHATSAPP" ? "WHATSAPP" : "SMS"; } catch { return "SMS"; } };

export function OtpChannelPicker({ className = "" }) {
  const { lang } = useLanguage();
  const T = PICK_TEXT[lang] || PICK_TEXT.en;
  const [ch, setCh] = useState(pickedOtpChannel);
  const pick = (v) => { setCh(v); try { localStorage.setItem(PICK_KEY, v); } catch { /* kept for this screen only */ } };
  return (
    <div className={className}>
      <p className="text-[10.5px] font-semibold text-[#6B7280] mb-1 pl-1">{T.via}</p>
      <div role="radiogroup" aria-label={T.via} className="grid grid-cols-2 gap-1 p-1 rounded-full bg-[#F1F5F9]">
        {[["WHATSAPP", T.wa], ["SMS", T.sms]].map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={ch === v} onClick={() => pick(v)}
            className={`h-[34px] rounded-full flex items-center justify-center gap-1.5 text-[11.5px] font-bold transition-all ${ch === v ? "bg-white text-[#0F172A] shadow-sm" : "text-[#475569]"}`}>
            {v === "WHATSAPP" ? <WaIcon /> : <span aria-hidden="true">💬</span>}{l}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Which way the server sent the code: WhatsApp only when it says so. */
export const channelOf = (res) => (res?.channel === "WHATSAPP" ? "WHATSAPP" : "SMS");

const fill = (s, v) => Object.entries(v).reduce((o, [k, x]) => o.split(`{${k}}`).join(String(x)), s);
const pretty = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : String(p || "");
};
const useText = () => { const { lang } = useLanguage(); return TEXT[lang] || TEXT.en; };

const WaIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 mt-[1px]">
    <path fill="#25D366" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" />
    <path fill="#fff" d="M8.6 7.3c.2-.4.4-.4.7-.4h.5c.2 0 .4 0 .6.5l.8 1.9c.1.2.1.4 0 .6l-.4.6-.4.4c-.1.1-.2.3-.1.5.4.8 1 1.5 1.7 2.1.8.7 1.5 1 1.8 1.1.2.1.4.1.5-.1l.7-.8c.2-.2.3-.2.5-.1l1.8.9c.2.1.4.2.4.3.1.2.1.9-.2 1.6-.3.7-1.6 1.4-2.2 1.4-.6.1-1.2.3-3.9-.8-3.2-1.3-5.2-4.6-5.4-4.8-.2-.2-1.3-1.7-1.3-3.3s.8-2.3 1.1-2.6z" />
  </svg>
);

/** ① / ③ - where the code went. Without a channel yet, the page's own line (`fallback`). */
export default function OtpChannelNote({ channel, phone, fallback = null }) {
  const T = useText();
  if (!channel) return fallback;
  const wa = channel === "WHATSAPP";
  return (
    <div role="status" className={`flex items-start gap-1.5 mb-1.5 px-2 py-1.5 rounded-[10px] border text-[10.5px] font-bold leading-snug ${
      wa ? "bg-[#ECFDF3] border-[#BBF7D0] text-[#14532D]" : "bg-[#F1F5F9] border-[#E2E8F0] text-[#1F2937]"}`}>
      {wa ? <WaIcon /> : <span aria-hidden="true" className="shrink-0">📩</span>}
      <span>{fill(wa ? T.wa : T.sms, { phone: pretty(phone) })}</span>
    </div>
  );
}

/** ② - after 20 seconds on WhatsApp, the way to SMS. Nothing once the code went by SMS. */
export function OtpSmsLink({ channel, sentAt, onSms }) {
  const T = useText();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (channel !== "WHATSAPP") return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [channel, sentAt]);
  if (channel !== "WHATSAPP") return null;
  const left = Math.max(0, SMS_AFTER_SECONDS - Math.floor((now - (sentAt || 0)) / 1000));
  if (left > 0) return <p className="mt-1.5 pl-1 text-[10px] text-[#64748B]">{fill(T.wait, { n: left })}</p>;
  return (
    <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { await onSms?.(); } finally { setBusy(false); } }}
      className="mt-1.5 pl-1 text-[10.5px] font-extrabold text-[#5E8700] underline disabled:opacity-50 text-left">
      {T.ask}
    </button>
  );
}
